"use client";
import React, { useEffect, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import gsap from 'gsap';

export default function PrivacyPolicyPage() {
  const pageRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // GSAP animations
  useEffect(() => {
    const ctx = gsap.context(() => {
      // Animate the page title
      gsap.fromTo(
        titleRef.current,
        { y: -20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8, ease: "power3.out" }
      );

      // Animate the content with a slight delay
      gsap.fromTo(
        contentRef.current,
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8, delay: 0.3, ease: "power2.out" }
      );
    }, pageRef);

    return () => ctx.revert();
  }, []);

  // Get current date for the "Last updated" section
  const currentDate = new Date().toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  return (
    <div ref={pageRef} className="container mx-auto px-4 py-12 max-w-6xl">
      <h1 ref={titleRef} className="text-3xl md:text-4xl font-bold mb-8 text-center">
        Privacy Policy
      </h1>
      
      <div ref={contentRef}>
        <Card className="mb-8">
          <CardContent className="p-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold">R&R Equipment Rentals</h2>
              <p>546 Burnside Rd, Gilberton QLD 4208</p>
              <p>Phone: 0477 111 731</p>
              <p className="mt-2 text-sm text-gray-500">Last updated: {currentDate}</p>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="overview" className="mb-12">
          <TabsList className="grid grid-cols-2 md:grid-cols-4 mb-8">
            <TabsTrigger value="overview">Overview & Collection</TabsTrigger>
            <TabsTrigger value="usage">Usage & Disclosure</TabsTrigger>
            <TabsTrigger value="rights">Your Rights</TabsTrigger>
            <TabsTrigger value="other">Other Information</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-4">1. Overview</h3>
                <p className="mb-4">
                  R&R Equipment Rentals ("we", "us", "our") is committed to protecting your privacy and personal information. 
                  This Privacy Policy outlines how we collect, store, use, and disclose personal data in accordance with the 
                  <strong> Privacy Act 1988 (Cth)</strong> and the <strong>Australian Privacy Principles (APPs)</strong>.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-4">2. What Information We Collect</h3>
                <p className="mb-4">We may collect the following types of personal information from you:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Full name, address, and contact details (email, phone number)</li>
                  <li>Driver's licence number and/or identity documentation</li>
                  <li>Company details (if hiring on behalf of a business)</li>
                  <li>Payment and billing details</li>
                  <li>Equipment hire history</li>
                  <li>Location data from equipment tracking (if applicable)</li>
                  <li>Website usage information via cookies and analytics tools</li>
                </ul>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-4">3. How We Collect Personal Information</h3>
                <p className="mb-4">We collect information through:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Direct contact (phone, email, website forms, in person)</li>
                  <li>Equipment booking or rental forms</li>
                  <li>Our website (including cookies and online tracking tools)</li>
                  <li>Third-party service providers, with your consent</li>
                </ul>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="usage" className="space-y-6">
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-4">4. Why We Collect Personal Information</h3>
                <p className="mb-4">We collect your information to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Provide equipment rental and associated services</li>
                  <li>Verify your identity and assess creditworthiness</li>
                  <li>Process payments and manage accounts</li>
                  <li>Comply with legal and regulatory obligations</li>
                  <li>Improve our services and website functionality</li>
                  <li>Send promotional material (only with your consent)</li>
                </ul>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-4">5. Disclosure of Personal Information</h3>
                <p className="mb-4">We may disclose your information to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Our employees, contractors, and suppliers to facilitate rental services</li>
                  <li>Payment processors and financial institutions</li>
                  <li>Legal, accounting, and insurance advisors</li>
                  <li>Law enforcement, courts, or government agencies when legally required</li>
                  <li>Third parties with your consent or as permitted under the Privacy Act</li>
                </ul>
                <p className="mt-4">We do not sell your personal information to third parties.</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-4">6. Security and Storage</h3>
                <p>
                  We take reasonable steps to protect your personal data from misuse, loss, and unauthorised access. 
                  Information is stored securely in physical files and/or electronic databases. Where no longer required, 
                  data is destroyed or de-identified.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="rights" className="space-y-6">
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-4">7. Accessing and Correcting Your Information</h3>
                <p>
                  You have the right to request access to your personal information and to request corrections if it is 
                  inaccurate, incomplete, or outdated. Please contact us using the details below to make such a request.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-4">8. Cookies and Website Analytics</h3>
                <p>
                  Our website may use cookies to improve your browsing experience and gather information about user behaviour. 
                  You can disable cookies in your browser settings, although this may affect website functionality.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-4">9. Marketing and Communication</h3>
                <p>
                  We may use your contact details to send you promotional materials. 
                  You can opt out at any time by clicking "unsubscribe" or contacting us directly.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="other" className="space-y-6">
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-4">10. Overseas Disclosure</h3>
                <p>
                  At present, we do not disclose personal information to overseas recipients. 
                  If this changes, we will update our policy and ensure compliance with applicable laws.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-4">11. Changes to This Policy</h3>
                <p>
                  We may update this Privacy Policy from time to time to reflect changes in law or our operations. 
                  The latest version will always be available on our website.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-4">12. Contact Us</h3>
                <p className="mb-4">
                  If you have any questions, concerns, or complaints about our handling of your personal information, 
                  or if you wish to exercise your rights under this policy, please contact:
                </p>
                <div className="pl-4 border-l-4 border-gray-200 py-2">
                  <p className="font-semibold">Privacy Officer</p>
                  <p>R&R Equipment Rentals</p>
                  <p>📍 546 Burnside Rd, Gilberton QLD 4208</p>
                  <p>📞 0477 111 731</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        
        <div className="text-center">
          <Separator className="my-8" />
          <p className="text-sm text-gray-500">
            This Privacy Policy is intended for informational purposes and does not constitute legal advice.
          </p>
        </div>
      </div>
    </div>
  );
}